import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["StepViewComponent", "showProgressIndicator", "showLogo"];
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { getFullUrl } from 'hubspot-url-utils';
import { Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LayoutTemplate } from 'signup-components/components/LayoutTemplate/LayoutTemplate';
import { LayoutType } from 'signup-components/types';
import { useSignupEmbedder } from 'signup-embedder/hooks/useSignupEmbedder';
import styled from 'styled-components';
import { useChatBot } from '../chatbot/useChatBot';
import { SignupChatbotStyles } from '../chatbot/ChatbotLayoutStyles';
import { getFlowConfig } from '../flow-initial/getFlowConfig';
import { getCurrentStepNumbering } from '../flow-management/stepNumberingSelectors';
import { useCanGoBack } from '../hooks/useCanGoBack';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { useIsSU0004Variation } from '../hooks/useIsSU0004Variation';
import { getLoginUrl } from '../http-login/getLoginUrl';
import { useMessage } from '../messaging/useMessage';
import { useResponsiveness } from '../responsiveness/ResponsivenessProvider';
import { getHaveAnAccountMessage } from '../step-landing-page/HaveAnAccountLink';
import { getLoginRedirectUrl } from '../step-landing-page/getLoginRedirectUrl';
import { goPrevStep } from '../store/flow/action-creators';
import { getPostAccountCreationSteps, isHSFXFLow } from '../store/flow/selectors';
import { getActiveLoading } from '../store/loading-status/selectors';
import { useTracker } from '../tracking/useTracker';
import { getStepConfig } from '../views/getStepConfig';
import { AppMessagingComponent } from './AppMessagingComponent';
import { Step } from 'signup-constants/Step';
import { SidePanelLayoutViewContainer } from './SidePanelLayoutViewContainer';
import { LoadingKey } from '../app-active-loading/LoadingKey';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import imageUrls from '../images/imageUrls';
import { isAssignedToGTKMExperiment } from '../utils/gtkm/isAssignedToGTKM';
import { SprocketIcon } from 'foundations-assets/icons/components/SprocketIcon';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Button } from 'foundations-components/transitional/components/Button';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const MessagingAboveStepContentContainer = styled.div.withConfig({
  displayName: "LayoutTemplateViewContainer__MessagingAboveStepContentContainer"
})(["display:flex;justify-content:center;"]);
const LayoutContainerWrapper = styled.div.withConfig({
  displayName: "LayoutTemplateViewContainer__LayoutContainerWrapper"
})(["", ""], props => props.$hasFixedHeader && `position: relative;`);
const FixedHeaderContainer = styled.div.withConfig({
  displayName: "LayoutTemplateViewContainer__FixedHeaderContainer"
})(["position:absolute;top:", ";left:", ";z-index:1;"], ({
  theme
}) => theme.space['600'], ({
  theme
}) => theme.space['1000']);
export const LayoutTemplateViewContainer = _ref => {
  let {
      StepViewComponent,
      showProgressIndicator = true,
      showLogo = true
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isActive: isInstanceOfSignupEmbedder,
    isEmbedded
  } = useSignupEmbedder();
  const {
    currentStep
  } = useCurrentStep();
  const loginRedirectUrl = getLoginRedirectUrl();
  const {
    canGoBack,
    completedStep
  } = useCanGoBack();
  const dispatch = useDispatch();
  const {
    currentStep: currentStepNumber,
    totalSteps
  } = useSelector(getCurrentStepNumbering);
  const activeLoading = useSelector(getActiveLoading);
  const {
    component
  } = useMessage();
  const Tracker = useTracker();
  const {
    isMobile
  } = useResponsiveness();
  const {
    url,
    altText,
    width: logoWidth,
    height: logoHeight
  } = getFlowConfig().getAppLogo(isMobile);
  const isHSFX = useSelector(isHSFXFLow);
  const customLogo = {
    src: url || (isHSFX ? imageUrls.trellisHsfxLogo : imageUrls.trellisLogo),
    alt: altText,
    width: logoWidth,
    height: logoHeight
  };
  const stepConfig = getStepConfig(currentStep);
  const showMessagingAboveContent = stepConfig && stepConfig.showMessagingAboveContent;
  const {
    layoutType,
    shouldShowProgressBar
  } = useLayoutTemplate();
  const isSU0004Variation = useIsSU0004Variation();
  const isSU0004LandingPage = isSU0004Variation && currentStep === Step.LandingPage;
  const isSU0004ReviewStep = isSU0004Variation && currentStep === Step.Review;
  const isSU0004NonLandingStep = isSU0004Variation && currentStep !== Step.LandingPage;
  useChatBot();
  const shouldShowSidePanel = layoutType === LayoutType.SidePanel;
  const sidePanelHideManageCookiesLink = shouldShowSidePanel && activeLoading;
  const isAssignedToGTKM = useSelector(isAssignedToGTKMExperiment);
  const alignCentredStepAtTheTop = isEmbedded && !activeLoading || stepConfig && stepConfig.alignCentredStepAtTheTop && !activeLoading;
  const isMobileHubletStep = currentStep === Step.HubletSelection && isMobile;
  const shouldShowBackLink = Boolean(canGoBack && completedStep);
  const onBackLinkClick = event => {
    event.preventDefault();
    Tracker.backButton(currentStep, completedStep);
    dispatch(goPrevStep());
  };
  const onAccountLinkClick = () => {
    Tracker.signupInteraction('click-login-have-an-account', {}, true);
    Tracker.redirect({
      url: `${getFullUrl('app')}/login`
    });
  };
  const onLogoClick = () => {
    Tracker.signupInteraction('click-hubspot-company-logo', {
      currentStep
    }, true);
  };
  const renderMessagingAboveStepContent = () => {
    if (showMessagingAboveContent) {
      return /*#__PURE__*/_jsx(MessagingAboveStepContentContainer, {
        children: /*#__PURE__*/_jsx(AppMessagingComponent, {})
      });
    }
    return null;
  };
  const postAccountCreation = useSelector(getPostAccountCreationSteps);
  const isPostAccountCreationStep = postAccountCreation.includes(currentStep);
  const shouldShowManageCookiesLink = !isInstanceOfSignupEmbedder && !isPostAccountCreationStep && activeLoading !== LoadingKey.BlankScreenLoading && !sidePanelHideManageCookiesLink;
  const getOptionalTitleContainer = () => {
    if (stepConfig && stepConfig.titleContainer) {
      return stepConfig.titleContainer;
    }
    return undefined;
  };
  const shouldShowLogo = showLogo && stepConfig && stepConfig.showLogo && !isSU0004Variation;
  if (layoutType === LayoutType.SidePanel) {
    return /*#__PURE__*/_jsx(SidePanelLayoutViewContainer, {
      currentStepNumber: currentStepNumber,
      customLogo: customLogo,
      totalSteps: totalSteps,
      children: /*#__PURE__*/_jsx(Suspense, {
        fallback: isAssignedToGTKM ? /*#__PURE__*/_jsx(TFlex, {
          alignItems: "center",
          "data-test-id": "loading-container",
          children: /*#__PURE__*/_jsx(LoadingSpinner, {
            layout: "centered",
            showLabel: true,
            size: "md"
          })
        }) : /*#__PURE__*/_jsx(LoadingSpinner, {
          "data-test-id": "loading-container",
          grow: true,
          size: "extra-small"
        }),
        children: /*#__PURE__*/_jsx(StepViewComponent, {})
      })
    });
  }
  return /*#__PURE__*/_jsxs(LayoutContainerWrapper, Object.assign({}, props, {
    $hasFixedHeader: isSU0004NonLandingStep
  }, isSU0004Variation ? {
    'data-layout': 'su0004'
  } : {}, {
    children: [isSU0004Variation && /*#__PURE__*/_jsx(SignupChatbotStyles, {}), isSU0004NonLandingStep && /*#__PURE__*/_jsx(FixedHeaderContainer, {
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "600",
        children: [/*#__PURE__*/_jsx(SprocketIcon, {
          "aria-label": "HubSpot",
          color: "fill-brand-default",
          size: "900"
        }), shouldShowBackLink && /*#__PURE__*/_jsxs(Button, {
          size: "extra-small",
          type: "button",
          use: "transparent",
          onClick: onBackLinkClick,
          children: [/*#__PURE__*/_jsx(LeftIcon, {
            size: "300"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "button.back"
          })]
        })]
      })
    }), /*#__PURE__*/_jsxs(LayoutTemplate, {
      accountLinkHref: getLoginUrl({
        redirectUrl: loginRedirectUrl
      }),
      accountLinkMessage: getHaveAnAccountMessage(),
      alignCentredStepAtTheTop: alignCentredStepAtTheTop && !isMobileHubletStep,
      currentStepNumber: currentStepNumber,
      layoutType: layoutType,
      logoAltText: altText,
      logoHeight: logoHeight,
      logoImageSrc: customLogo.src,
      logoWidth: logoWidth,
      messagingContent: component && component(),
      shouldAddTopPadding: !isEmbedded && !isSU0004LandingPage && !isSU0004ReviewStep,
      shouldHalveProgressBar: getFlowConfig().shouldHalveProgressBar,
      shouldLogoBeClickable: false,
      showBackLink: shouldShowBackLink && !isSU0004NonLandingStep,
      showHaveAnAccountLink: false,
      showLogo: shouldShowLogo,
      showManageCookiesLink: shouldShowManageCookiesLink,
      showProgressIndicator: showProgressIndicator && shouldShowProgressBar,
      titleContainer: getOptionalTitleContainer(),
      totalSteps: totalSteps,
      onAccountLinkClick: onAccountLinkClick,
      onBackLinkClick: onBackLinkClick,
      onLogoClick: onLogoClick,
      children: [renderMessagingAboveStepContent(), /*#__PURE__*/_jsx(Suspense, {
        fallback: /*#__PURE__*/_jsx(LoadingSpinner, {
          "data-test-id": "loading-container",
          grow: true,
          size: "extra-small"
        }),
        children: /*#__PURE__*/_jsx(StepViewComponent, {})
      })]
    })]
  }));
};